import React, { useState, useEffect, useContext } from "react";

import {
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Stack,
  Button,
  TextField,
} from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import { isEmpty, isUndefined } from "lodash";
import moment from "moment";
import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import NoData from "../../../components/NoData";
import { getCajaMovAsync } from "../../../api/Cash";
import ListFooter from "../../../components/ui/ListFooter";
import SmallModal from "../../../components/modals/SmallModal";
import AddCajaMovment from "./AddCajaMovment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { monoFont } from "../../../theme";

const money = (n) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(n);

// La sucursal ya no se elige aqui: viene del selector global del topbar
// (DataContext.selectedSucursal), como pide el README del handoff.
const Caja = () => {
  const [cajaMovList, setCajaMovList] = useState([]);
  const [localReload, setLocalReload] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [isEntrada, setIsEntrada] = useState(false);

  var date = new Date();
  const [fechaDesde, setFechaDesde] = useState(
    new Date(date.getFullYear(), date.getMonth(), 1)
  );
  const [fechaHasta, setFechaHasta] = useState(date);

  const { setIsLoading, setIsUnautorized, access, selectedSucursal } =
    useContext(DataContext);
  const token = getToken();

  useEffect(() => {
    if (selectedSucursal === 0 || isUndefined(selectedSucursal)) {
      return;
    }
    getCajaMovs(selectedSucursal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSucursal, localReload, fechaDesde, fechaHasta]);

  const addMovment = (value) => {
    setIsEntrada(value);
    setShowModal(true);
  };

  const getCajaMovs = async (sucursal) => {
    setIsLoading(true);
    const data = {
      desde: moment(fechaDesde).format("YYYY-MM-DD"),
      hasta: moment(fechaHasta).format("YYYY-MM-DD"),
      sucursalId: sucursal,
    };
    const result = await getCajaMovAsync(token, data);
    if (!result.statusResponse) {
      setIsLoading(false);
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
      return;
    }
    setIsLoading(false);

    setCajaMovList(result.data.result);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsperPage] = useState(10);
  const indexLast = currentPage * itemsperPage;
  const indexFirst = indexLast - itemsperPage;
  const currentItem = cajaMovList.slice(indexFirst, indexLast);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nombreCorto = (fullName) => {
    const partes = (fullName || "").split(" ");
    return `${partes[0] || ""} ${partes[1] || ""}`.trim();
  };

  return (
    <div>
      <Paper sx={{ overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", lg: "center" }}
          spacing={2}
          flexWrap="wrap"
          sx={{ padding: "18px 22px", borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <DesktopDatePicker
              closeOnSelect
              label="Desde"
              inputFormat="DD/MM/YYYY"
              value={fechaDesde}
              onChange={(newValue) => setFechaDesde(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />

            <DesktopDatePicker
              closeOnSelect
              label="Hasta"
              inputFormat="DD/MM/YYYY"
              value={fechaHasta}
              onChange={(newValue) => setFechaHasta(newValue)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
          </Stack>

          {isAccess(access, "CAJA CREATE") ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                onClick={() => addMovment(true)}
                startIcon={<AddCircleOutlineRoundedIcon />}
                variant="contained"
                color="primary"
                sx={{ whiteSpace: "nowrap", height: "44px" }}
              >
                Entrada
              </Button>
              <Button
                onClick={() => addMovment(false)}
                startIcon={<RemoveCircleOutlineRoundedIcon />}
                variant="outlined"
                color="error"
                sx={{ whiteSpace: "nowrap", height: "44px" }}
              >
                Salida
              </Button>
            </Stack>
          ) : null}
        </Stack>

        {isEmpty(currentItem) ? (
          <NoData />
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Fecha</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">Entrada</TableCell>
                    <TableCell align="right">Salida</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Realizado por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItem.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center" sx={{ color: "text.secondary" }}>
                        {moment(row.fecha).format("L")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>{row.description}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: monoFont, color: "success.main", whiteSpace: "nowrap" }}
                      >
                        {money(row.entradas)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: monoFont, color: "error.main", whiteSpace: "nowrap" }}
                      >
                        {money(row.salidas)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: monoFont, fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}
                      >
                        {money(row.saldo)}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {nombreCorto(row.realizadoPor ? row.realizadoPor.fullName : "")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <ListFooter
              data={cajaMovList}
              currentCount={currentItem.length}
              indexFirst={indexFirst}
              paginate={paginate}
              itemsperPage={itemsperPage}
              noun="movimientos"
            />
          </>
        )}
      </Paper>

      <SmallModal
        titulo={
          isEntrada
            ? "Entrada de Efectivo a Caja"
            : "Salida de Efectivo de Caja"
        }
        isVisible={showModal}
        setVisible={setShowModal}
      >
        <AddCajaMovment
          setShowModal={setShowModal}
          isEntrada={isEntrada}
          localReload={localReload}
          setLocalReload={setLocalReload}
        />
      </SmallModal>
    </div>
  );
};

export default Caja;
